/**
 * Category stock-photo registry, wired into `resolveRecoImageSrc.js` as the image
 * source for every reco card. Product photos aren't scraped from Amazon (see
 * `SHOW_AMAZON_IMAGES` in `../config/amazonDataAvailability.js`) — instead every
 * product in a preset interest bucket shares one representative photo (e.g. every
 * wireless mouse under "Gaming" uses the same controller photo) until real
 * photography from the Amazon Product Advertising API is available.
 *
 * Sourcing: photos are pulled from Wikimedia Commons (each under a free license —
 * Public Domain, CC0, or Creative Commons Attribution/Attribution-ShareAlike) plus
 * a handful of originally-curated Unsplash License photos (free for commercial +
 * non-commercial use, no attribution required — https://unsplash.com/license).
 * `credit`/`creditUrl` record the photographer + source page for any photo that
 * requires attribution; double-check licensing again for any photo swapped in
 * later from a different source.
 *
 * Keys are the preset interest labels from `interestGroups.js` so this can't drift
 * from the single interest catalog (see AGENTS.md "Giftee interests" note).
 */
import { PRESET_INTEREST_LABELS } from "./interestGroups.js";

/**
 * @typedef {{ url: string, credit: string, creditUrl?: string }} StockImageEntry
 */

/** One representative, freely-licensed photo per preset interest label. */
const CURATED_STOCK_IMAGES = {
  Coffee: {
    url: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=640&q=80",
    credit: "Unsplash",
  },
  Tea: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3c/Cup_of_tea_Pitta_Corner_Wood_Green_High_Road_Haringey_London_England_03.jpg/960px-Cup_of_tea_Pitta_Corner_Wood_Green_High_Road_Haringey_London_England_03.jpg",
    credit: "Acabashi via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl:
      "https://commons.wikimedia.org/wiki/File:Cup_of_tea_Pitta_Corner_Wood_Green_High_Road_Haringey_London_England_03.jpg",
  },
  Wine: {
    url: "https://images.unsplash.com/photo-1600673177531-46749442aa63?auto=format&fit=crop&w=640&q=80",
    credit: "Unsplash",
  },
  Cocktails: {
    url: "https://upload.wikimedia.org/wikipedia/commons/9/96/CocktailStrainer_with_Glass2.jpg",
    credit: "ks aka 98 via Wikimedia Commons (Public domain)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:CocktailStrainer_with_Glass2.jpg",
  },
  Snacks: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8f/A_bowl_of_snacks.jpg/960px-A_bowl_of_snacks.jpg",
    credit: "Toby Scratch Fanatic via Wikimedia Commons (CC0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:A_bowl_of_snacks.jpg",
  },
  Baking: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f1/Bread%2C_fresh_from_the_oven_-_Massachusetts.jpg/960px-Bread%2C_fresh_from_the_oven_-_Massachusetts.jpg",
    credit: "Daderot via Wikimedia Commons (CC0)",
    creditUrl:
      "https://commons.wikimedia.org/wiki/File:Bread,_fresh_from_the_oven_-_Massachusetts.jpg",
  },
  BBQ: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d4/Brochettes_de_poulet_marin%C3%A9_en_fin_de_cuisson_au_barbecue_%28avril_2020%29.jpg/960px-Brochettes_de_poulet_marin%C3%A9_en_fin_de_cuisson_au_barbecue_%28avril_2020%29.jpg",
    credit: "Benoît Prieur via Wikimedia Commons (CC0)",
    creditUrl:
      "https://commons.wikimedia.org/wiki/File:Brochettes_de_poulet_marin%C3%A9_en_fin_de_cuisson_au_barbecue_(avril_2020).jpg",
  },
  Chocolate: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/23/2020-02-23_02_23_53_A_Ghirardelli_Dark_Chocolate_Mint_Square_in_the_Parkway_Village_section_of_Ewing_Township%2C_Mercer_County%2C_New_Jersey.jpg/960px-2020-02-23_02_23_53_A_Ghirardelli_Dark_Chocolate_Mint_Square_in_the_Parkway_Village_section_of_Ewing_Township%2C_Mercer_County%2C_New_Jersey.jpg",
    credit: "Famartin via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl:
      "https://commons.wikimedia.org/wiki/File:2020-02-23_02_23_53_A_Ghirardelli_Dark_Chocolate_Mint_Square_in_the_Parkway_Village_section_of_Ewing_Township,_Mercer_County,_New_Jersey.jpg",
  },
  Running: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a7/Running_shoes.jpg/960px-Running_shoes.jpg",
    credit: "Tiia Monto via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:Running_shoes.jpg",
  },
  Biking: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3e/Closeup_photo_of_Bicycle_wheel.jpg/960px-Closeup_photo_of_Bicycle_wheel.jpg",
    credit: "Tajdikul alam niloy via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:Closeup_photo_of_Bicycle_wheel.jpg",
  },
  Yoga: {
    url: "https://images.unsplash.com/photo-1601925260368-ae2f83cf8b7f?auto=format&fit=crop&w=640&q=80",
    credit: "Unsplash",
  },
  Strength: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/50/A_close-up_view_of_a_set_of_heavy_dumbbells.jpg/960px-A_close-up_view_of_a_set_of_heavy_dumbbells.jpg",
    credit: "Shixart1985 via Wikimedia Commons (CC BY 2.0)",
    creditUrl:
      "https://commons.wikimedia.org/wiki/File:A_close-up_view_of_a_set_of_heavy_dumbbells.jpg",
  },
  Recovery: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/41/Hartschaumrolle.jpg/960px-Hartschaumrolle.jpg",
    credit: "Finfit via Wikimedia Commons (CC BY-SA 3.0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:Hartschaumrolle.jpg",
  },
  Headphones: {
    url: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&w=640&q=80",
    credit: "Unsplash",
  },
  Audio: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9e/Sonos_PLAY_1_wireless_speaker_%28cropped%29.jpg/960px-Sonos_PLAY_1_wireless_speaker_%28cropped%29.jpg",
    credit: "Robert Wetzlmayr via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl:
      "https://commons.wikimedia.org/wiki/File:Sonos_PLAY_1_wireless_speaker_(cropped).jpg",
  },
  "Smart Home": {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/14/Red_Echo_Dot_%28Side%29.jpg/960px-Red_Echo_Dot_%28Side%29.jpg",
    credit: "DiscoA340 via Wikimedia Commons (CC0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:Red_Echo_Dot_(Side).jpg",
  },
  Photography: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1e/AF-S_DX_Nikkor_18-140_f_3.5-5.6G_ED_VR.jpg/960px-AF-S_DX_Nikkor_18-140_f_3.5-5.6G_ED_VR.jpg",
    credit: "Carlos Valenzuela via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl:
      "https://commons.wikimedia.org/wiki/File:AF-S_DX_Nikkor_18-140_f_3.5-5.6G_ED_VR.jpg",
  },
  "Desk Setup": {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c3/White_keyboard_on_a_modern_desk_closeup.jpg/960px-White_keyboard_on_a_modern_desk_closeup.jpg",
    credit: "Shixart1985 via Wikimedia Commons (CC BY 2.0)",
    creditUrl:
      "https://commons.wikimedia.org/wiki/File:White_keyboard_on_a_modern_desk_closeup.jpg",
  },
  Gaming: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4d/Xbox-360-Wireless-Controller-White.jpg/960px-Xbox-360-Wireless-Controller-White.jpg",
    credit: "Evan-Amos via Wikimedia Commons (Public domain)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:Xbox-360-Wireless-Controller-White.jpg",
  },
  EDC: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/6/64/MiniUtility4.JPG/960px-MiniUtility4.JPG",
    credit: "Simon Speed via Wikimedia Commons (CC0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:MiniUtility4.JPG",
  },
  Candles: {
    url: "https://images.unsplash.com/photo-1570823635306-250abb06d4b3?auto=format&fit=crop&w=640&q=80",
    credit: "Unsplash",
  },
  Blankets: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/01/9Y3A0940_%281%29.jpg/960px-9Y3A0940_%281%29.jpg",
    credit: "Loveyourmelon via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:9Y3A0940_(1).jpg",
  },
  Mugs: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fb/Atom_Editor_coffee_mug%2C_2025-05-04.jpeg/960px-Atom_Editor_coffee_mug%2C_2025-05-04.jpeg",
    credit: "Nylki via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:Atom_Editor_coffee_mug,_2025-05-04.jpeg",
  },
  Plants: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/00/Lidcactus%2C_Schlumbergera_%C3%97buckleyi._14-12-2025._%28actm.%29_01.jpg/960px-Lidcactus%2C_Schlumbergera_%C3%97buckleyi._14-12-2025._%28actm.%29_01.jpg",
    credit: "Agnes Monkelbaan via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl:
      "https://commons.wikimedia.org/wiki/File:Lidcactus,_Schlumbergera_%C3%97buckleyi._14-12-2025._(actm.)_01.jpg",
  },
  Organization: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7c/1990s_Dynoplast_Norway_stackable_plastic_storage_bin_model_6734.jpg/960px-1990s_Dynoplast_Norway_stackable_plastic_storage_bin_model_6734.jpg",
    credit: "R. Henrik Nilsson via Wikimedia Commons (CC BY 4.0)",
    creditUrl:
      "https://commons.wikimedia.org/wiki/File:1990s_Dynoplast_Norway_stackable_plastic_storage_bin_model_6734.jpg",
  },
  "Kitchen Gadgets": {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a9/Salt_and_Pepper_Shaker_Museum_Gatlinburg.jpg/960px-Salt_and_Pepper_Shaker_Museum_Gatlinburg.jpg",
    credit: "Aludden1 via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl:
      "https://commons.wikimedia.org/wiki/File:Salt_and_Pepper_Shaker_Museum_Gatlinburg.jpg",
  },
  Camping: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9d/Airconditioned_Luxury_tents_at_%27Lion_Safari_Camp%27_resort_in_Gir_Forest_of_Gujarat..JPG/960px-Airconditioned_Luxury_tents_at_%27Lion_Safari_Camp%27_resort_in_Gir_Forest_of_Gujarat..JPG",
    credit: "Rudolph.A.furtado via Wikimedia Commons (CC0)",
    creditUrl:
      "https://commons.wikimedia.org/wiki/File:Airconditioned_Luxury_tents_at_%27Lion_Safari_Camp%27_resort_in_Gir_Forest_of_Gujarat..JPG",
  },
  Hiking: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/12/Wandern_im_Grand_Canyon.jpg/960px-Wandern_im_Grand_Canyon.jpg",
    credit: "Jensre via Wikimedia Commons (Public domain)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:Wandern_im_Grand_Canyon.jpg",
  },
  Fishing: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/12/A_spinning_reel_on_a_rod.jpg/960px-A_spinning_reel_on_a_rod.jpg",
    credit: "JaredMcKenzie via Wikimedia Commons (CC0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:A_spinning_reel_on_a_rod.jpg",
  },
  Gardening: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/76/Steel_trowel.jpg/960px-Steel_trowel.jpg",
    credit: "Qurren via Wikimedia Commons (CC BY-SA 3.0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:Steel_trowel.jpg",
  },
  "Travel Gear": {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/17/Belber_Crocodile_Trunks_and_Luggage.jpg/960px-Belber_Crocodile_Trunks_and_Luggage.jpg",
    credit: "Sandrine Z via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl:
      "https://commons.wikimedia.org/wiki/File:Belber_Crocodile_Trunks_and_Luggage.jpg",
  },
  Outdoors: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/eb/Adventure_Life_Outdoor_Gear_%282023-10-27%29.jpg/960px-Adventure_Life_Outdoor_Gear_%282023-10-27%29.jpg",
    credit: "PaulGorduiz106 via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl:
      "https://commons.wikimedia.org/wiki/File:Adventure_Life_Outdoor_Gear_(2023-10-27).jpg",
  },
  Fiction: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/bc/Pile_of_books_about_Wicca_2.JPG/960px-Pile_of_books_about_Wicca_2.JPG",
    credit: "HelenOnline via Wikimedia Commons (CC BY-SA 3.0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:Pile_of_books_about_Wicca_2.JPG",
  },
  Nonfiction: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/23/Aufgeschlagenes_Buch_--_2020_--_4195-201.jpg/960px-Aufgeschlagenes_Buch_--_2020_--_4195-201.jpg",
    credit: "Dietmar Rabich via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl:
      "https://commons.wikimedia.org/wiki/File:Aufgeschlagenes_Buch_--_2020_--_4195-201.jpg",
  },
  Puzzles: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0b/Charles_Wysocki_jigsaw_Puzzles.jpg/960px-Charles_Wysocki_jigsaw_Puzzles.jpg",
    credit: "Elwood P. Dowd via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:Charles_Wysocki_jigsaw_Puzzles.jpg",
  },
  Journaling: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3e/Notebook_with_pen%2C_August%2C_2019.jpg/960px-Notebook_with_pen%2C_August%2C_2019.jpg",
    credit: "Phil Slattery via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:Notebook_with_pen,_August,_2019.jpg",
  },
  Audiobooks: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d6/Here_One_earbuds_in_white_charging_case.jpg/960px-Here_One_earbuds_in_white_charging_case.jpg",
    credit: "Doppler Labs via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl:
      "https://commons.wikimedia.org/wiki/File:Here_One_earbuds_in_white_charging_case.jpg",
  },
  "Language Learning": {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/ae/Oxford_Dictionary_of_National_Biography_volumes.jpg/960px-Oxford_Dictionary_of_National_Biography_volumes.jpg",
    credit: "Mgoutsidou via Wikimedia Commons (CC BY-SA 3.0)",
    creditUrl:
      "https://commons.wikimedia.org/wiki/File:Oxford_Dictionary_of_National_Biography_volumes.jpg",
  },
  Drawing: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/09/Colored_wood_pencils_01.jpg/960px-Colored_wood_pencils_01.jpg",
    credit: "Pinakpani via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:Colored_wood_pencils_01.jpg",
  },
  Knitting: {
    url: "https://upload.wikimedia.org/wikipedia/commons/7/76/Single_pointed_knitting_needle.jpg",
    credit: "Shalni verma via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:Single_pointed_knitting_needle.jpg",
  },
  DIY: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Repair_tool_kit.jpg/960px-Repair_tool_kit.jpg",
    credit: "Wilfredor via Wikimedia Commons (CC0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:Repair_tool_kit.jpg",
  },
  Stationery: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/26/%D0%9A%D0%B0%D0%BD%D1%86%D0%B5%D0%BB%D1%8F%D1%80%D1%81%D0%BA%D0%B8%D0%B5_%D1%82%D0%BE%D0%B2%D0%B0%D1%80%D1%8B_Erich_Krause.JPG/960px-%D0%9A%D0%B0%D0%BD%D1%86%D0%B5%D0%BB%D1%8F%D1%80%D1%81%D0%BA%D0%B8%D0%B5_%D1%82%D0%BE%D0%B2%D0%B0%D1%80%D1%8B_Erich_Krause.JPG",
    credit: "Schekinov Alexey Victorovich via Wikimedia Commons (CC BY-SA 3.0)",
    creditUrl:
      "https://commons.wikimedia.org/wiki/File:%D0%9A%D0%B0%D0%BD%D1%86%D0%B5%D0%BB%D1%8F%D1%80%D1%81%D0%BA%D0%B8%D0%B5_%D1%82%D0%BE%D0%B2%D0%B0%D1%80%D1%8B_Erich_Krause.JPG",
  },
  Painting: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/43/Colorful_art_supplies_arranged_on_a_table_featuring_paints%2C_markers%2C_and_paper_ready_for_creative_projects_during_a_sunny_afternoon.jpg/960px-Colorful_art_supplies_arranged_on_a_table_featuring_paints%2C_markers%2C_and_paper_ready_for_creative_projects_during_a_sunny_afternoon.jpg",
    credit: "Shixart1985 via Wikimedia Commons (CC BY 2.0)",
    creditUrl:
      "https://commons.wikimedia.org/wiki/File:Colorful_art_supplies_arranged_on_a_table_featuring_paints,_markers,_and_paper_ready_for_creative_projects_during_a_sunny_afternoon.jpg",
  },
  Jewelry: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f2/Inca_-_gold_ring.jpg/960px-Inca_-_gold_ring.jpg",
    credit: "W.carter via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:Inca_-_gold_ring.jpg",
  },
  Fragrance: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/79/Cordon_Rouge_-_Cologne_by_Coty.jpg/960px-Cordon_Rouge_-_Cologne_by_Coty.jpg",
    credit: "DDupard via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:Cordon_Rouge_-_Cologne_by_Coty.jpg",
  },
  Accessories: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/19/Brown_leather_purse_detail.jpg/960px-Brown_leather_purse_detail.jpg",
    credit: "DariaPerse via Wikimedia Commons (CC BY-SA 3.0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:Brown_leather_purse_detail.jpg",
  },
  Skincare: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0f/Day_cream_02.jpg/960px-Day_cream_02.jpg",
    credit: "Benff via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:Day_cream_02.jpg",
  },
  Loungewear: {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/6/66/Cashmere_Sweater.jpg/960px-Cashmere_Sweater.jpg",
    credit: "Ssblucky7 via Wikimedia Commons (CC BY-SA 4.0)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:Cashmere_Sweater.jpg",
  },
  Dogs: {
    url: "https://images.unsplash.com/photo-1750967028589-e303192ebfb3?auto=format&fit=crop&w=640&q=80",
    credit: "Unsplash",
  },
  Cats: {
    url: "https://images.unsplash.com/photo-1773394472699-e12c3f49f659?auto=format&fit=crop&w=640&q=80",
    credit: "Unsplash",
  },
  "Small Pets": {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0b/Hamster_on_Wheel.jpg/960px-Hamster_on_Wheel.jpg",
    credit: "Wikimedia Commons contributor (Public domain)",
    creditUrl: "https://commons.wikimedia.org/wiki/File:Hamster_on_Wheel.jpg",
  },
  "Pet Parent Gear": {
    url: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/bf/HK_Peak_Galleria_%E5%B1%B1%E9%A0%82%E5%BB%A3%E5%A0%B4_Sunday_dog_walking_01.JPG/960px-HK_Peak_Galleria_%E5%B1%B1%E9%A0%82%E5%BB%A3%E5%A0%B4_Sunday_dog_walking_01.JPG",
    credit: "Snowacinesy via Wikimedia Commons (CC BY-SA 3.0)",
    creditUrl:
      "https://commons.wikimedia.org/wiki/File:HK_Peak_Galleria_%E5%B1%B1%E9%A0%82%E5%BB%A3%E5%A0%B4_Sunday_dog_walking_01.JPG",
  },
};

/**
 * Preset interest label -> {@link StockImageEntry}. Always has every label from
 * `PRESET_INTEREST_LABELS` as a key; falls back to the Coffee photo (extremely
 * unlikely to trigger — every label above is curated) so a missing entry never
 * renders a broken image.
 * @type {Record<string, StockImageEntry>}
 */
export const STOCK_PRODUCT_IMAGES = Object.fromEntries(
  Array.from(PRESET_INTEREST_LABELS, (label) => [
    label,
    CURATED_STOCK_IMAGES[label] ?? CURATED_STOCK_IMAGES.Coffee,
  ]),
);
